/**
 * 全局视图标识。
 *
 * `work` 是「工作区」：写当日笔记 + 看当日新闻，与 `note`（跨笔记浏览检索）
 * 并存 —— 后者的全文检索与标签筛选是独立能力，不应被前者取代。
 * 导航已统一到顶栏，故不再有「桌面/移动端各自的视图集合」。
 */
export type ViewId = 'market' | 'chart' | 'work' | 'note' | 'review';

export const TOPBAR_VIEWS: readonly ViewId[] =
  ['market', 'chart', 'work', 'note', 'review'] as const;

/** K线周期（需求 F2.1） */
export type Period = 'D' | 'W' | 'M' | 'Y' | 'MIN1' | 'MIN5';

/**
 * label 只用于 tab 显示（短，去掉「K」省横向空间，窄屏尤甚）；
 * full 用于悬停提示与无障碍名称，保留「日K」全称 —— 屏幕阅读器与
 * 悬停时仍能得知这是 K 线周期，而非其它含义的「日」。
 */
export const PERIODS: { id: Period; label: string; full: string; group: 1 | 2 }[] = [
  // 前 4 组基于日K聚合，后 2 组基于 tick 数据，语义不同，UI 上用分隔线区分
  { id: 'D', label: '日', full: '日K', group: 1 },
  { id: 'W', label: '周', full: '周K', group: 1 },
  { id: 'M', label: '月', full: '月K', group: 1 },
  { id: 'Y', label: '年', full: '年K', group: 1 },
  { id: 'MIN1', label: '分时', full: '分时', group: 2 },
  { id: 'MIN5', label: '5日', full: '5日', group: 2 },
];

/** 主图叠加指标 */
export type OverlayId = 'ma' | 'boll' | 'bbi' | 'valuation';

/**
 * MA 可选周期。
 *
 * 与 `chartTheme.ma` 的键（Record<5|10|20|60|120, string>）一一对应 ——
 * 增删周期时两处必须同步，否则配色取不到而回落到 undefined。
 * 改前 ChartCanvas 硬编码 `[5,10,20,60,120]` 全部画出、不可选，
 * 且 OverlayConfig 里那个「5▾」是**无 onClick 的静态文本**（死 UI）。
 */
export const MA_PERIODS = [5, 10, 20, 60, 120] as const;
export type MaPeriod = (typeof MA_PERIODS)[number];

/**
 * MA 常用组合 —— 弹层里一键全选/清空，省去逐个勾选。
 * 名称按行情软件的习惯叫法（短线/中线/长线/全部）。
 */
export const MA_PRESETS: { id: string; label: string; periods: MaPeriod[] }[] = [
  { id: 'short', label: '短线', periods: [5, 10] },
  { id: 'mid', label: '中线', periods: [5, 10, 20] },
  { id: 'long', label: '长线', periods: [20, 60, 120] },
  { id: 'all', label: '全部', periods: [...MA_PERIODS] },
  { id: 'none', label: '清空', periods: [] },
];

/** 副图指标（需求 F2.6：0–3 个可调） */
export type PaneId = 'VOL' | 'MACD' | 'KDJ' | 'RSI';

/** 副图数量上限 —— 与需求 F2.6「个数 0~3 可调」一致 */
export const PANE_MAX_COUNT = 3;

export const PANE_OPTIONS: { id: PaneId; label: string }[] = [
  { id: 'VOL', label: '成交量' },
  { id: 'MACD', label: 'MACD' },
  { id: 'KDJ', label: 'KDJ' },
  { id: 'RSI', label: 'RSI' },
];

/** 估值曲线类型与口径（需求 F2.5） */
export type ValuationKind = 'none' | 'pe' | 'dividend' | 'both';
export type ValuationBasis = 'pe1' | 'pe2'; // pe1=静态  pe2=TTM

/** 笔记 scope（需求 F5） */
export type NoteScope = 'day' | 'week' | 'month' | 'year';

export const NOTE_SCOPES: { id: NoteScope; label: string }[] = [
  { id: 'day', label: '日' },
  { id: 'week', label: '周' },
  { id: 'month', label: '月' },
  { id: 'year', label: '年' },
];

/** 情绪 */
export type Sentiment = 'bullish' | 'bearish' | 'neutral';

export const SENTIMENTS: { id: Sentiment; label: string; varName: string }[] = [
  { id: 'bullish', label: '看多', varName: '--up' },
  { id: 'bearish', label: '看空', varName: '--down' },
  { id: 'neutral', label: '中性', varName: '--flat' },
];